import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import type { ReactNode } from 'react'
import type { User } from '@/types'
import {
  getMe,
  logout as apiLogout,
  ssoLogin,
  fetchBridgeToken,
  isSsoSuppressed,
} from '@/api/auth'
import { setOnRefreshFailure } from '@/api/client'

// 'loading'         — bootstrap in flight (show spinner)
// 'authed'          — signed in (user is set)
// 'login'           — not signed in; show LoginPage (dev email/password login)
// 'not_provisioned' — valid ibase user but no Twasta tenant/user (dedicated msg)
export type AuthStatus = 'loading' | 'authed' | 'login' | 'not_provisioned'

interface AuthContextType {
  user: User | null
  authStatus: AuthStatus
  /** Why the bootstrap refused, straight from the backend — only set with
   *  'not_provisioned'. Null when there is nothing more specific to say. */
  authMessage: string | null
  // Back-compat derived flags (existing consumers rely on these).
  loading: boolean
  isAuthenticated: boolean
  login: (user: User) => void
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function statusFromError(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

function detailFromError(err: unknown): string | null {
  const d = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof d === 'string' && d.trim() ? d.trim() : null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading')
  const [authMessage, setAuthMessage] = useState<string | null>(null)

  // Guard: attempt the silent SSO bootstrap at most once per page load, so a
  // permanent failure (no ibase session, unprovisioned) can't loop. Reset only
  // when a token refresh fails (long-session drop) to allow a fresh re-bootstrap.
  const ssoAttempted = useRef(false)

  const refreshUser = useCallback(async () => {
    // A re-bootstrap must not show the previous attempt's reason.
    setAuthMessage(null)
    // Set (possibly to null) only when the bridge sign-in was REFUSED, so the
    // 'not_provisioned' screen is reached from a refusal and never from a
    // plain "no ibase session".
    let ssoRefusal: string | null | undefined

    // 1) Ask the bridge FIRST — when the SPA is served behind ibase, the ibase
    //    session is the authoritative identity. Trusting the Twasta cookie
    //    first meant a browser still carrying the PREVIOUS ibase user's
    //    cookies signed the NEW ibase user in as that previous user (ibase
    //    logout cannot clear Twasta's own httpOnly cookies), and every page —
    //    gen processes included — then rendered the wrong user's data.
    //    `/auth/sso` reconciles: the same identity keeps its session, a
    //    different one replaces it.
    //    Once per page load, unless suppressed (just logged out of Twasta).
    if (!ssoAttempted.current && !isSsoSuppressed()) {
      ssoAttempted.current = true
      try {
        const token = await fetchBridgeToken() // throws when no ibase session
        const data = await ssoLogin(token)      // 401 invalid token / 403 not provisioned
        setUser(data)
        setAuthStatus('authed')
        return
      } catch (err) {
        if (statusFromError(err) === 403) {
          // The backend distinguishes three refusals that need three different
          // fixes — unmapped enterprise, deactivated account, email bound to
          // another tenant. Keep its reason: one generic sentence for all three
          // leaves the user (and whoever they contact) with nothing to act on.
          ssoRefusal = detailFromError(err)
        }
        // Any other failure (no ibase session, no bridge, SSO disabled) just
        // falls through to the cookie session below.
      }
    }

    // 2) An existing Twasta session — a dev email/password login, or the
    //    session this page's own bridge sign-in established on an earlier pass.
    try {
      const data = await getMe()
      setUser(data)
      setAuthStatus('authed')
      return
    } catch {
      setUser(null)
    }

    // 3) A refused bridge sign-in with no Twasta session to fall back on gets
    //    the dedicated message; everything else gets the login screen (dev
    //    email/password login stays reachable).
    if (ssoRefusal !== undefined) {
      setAuthMessage(ssoRefusal)
      setAuthStatus('not_provisioned')
      return
    }
    setAuthStatus('login')
  }, [])

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  // A failed token refresh (access+refresh both expired on a long session)
  // resets the once-guard and re-bootstraps so the user can be silently
  // re-SSO'd instead of being dropped to the login page.
  useEffect(() => {
    setOnRefreshFailure(() => {
      ssoAttempted.current = false
      refreshUser()
    })
    return () => setOnRefreshFailure(null)
  }, [refreshUser])

  const login = useCallback((u: User) => {
    setUser(u)
    setAuthStatus('authed')
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiLogout() // also sets the sso_suppressed marker
    } catch {
      // Ignore logout errors
    }
    setUser(null)
    setAuthStatus('login')
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        authStatus,
        authMessage,
        loading: authStatus === 'loading',
        isAuthenticated: authStatus === 'authed' && !!user,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
